import type { BillingOverview } from '@tandemone/types'
import { PLAN_CATALOG } from '@tandemone/types'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { isStandaloneDisplay } from '@/domains/install'

import {
  formatBillingDate,
  formatMoney,
  intervalLabel,
  planPriceParts
} from './plan-price'

/* an unpaid first invoice resolves to no entitlements, so the plan reads Free
 * and only the open bill distinguishes "waiting to pay" from "never bought". */
function StatusBadge({ overview }: { overview: BillingOverview }) {
  const ent = overview.entitlements
  if (ent.status === 'past_due') {
    return (
      <Badge className='border-transparent bg-amber-100 text-amber-900'>
        Payment failed
      </Badge>
    )
  }
  /* an open invoice outranks the approval wording: both states are pending,
   * but this one is already approved and waiting on money. */
  if (ent.status === 'none' && overview.invoiceUrl) {
    return (
      <Badge className='border-transparent bg-amber-100 text-amber-900'>
        Awaiting payment
      </Badge>
    )
  }
  if (ent.status === 'none' && overview.pendingPlanId) {
    return (
      <Badge className='border-transparent bg-sky-100 text-sky-900'>
        Starts on approval
      </Badge>
    )
  }
  if (ent.cancelAtPeriodEnd) {
    return (
      <Badge className='border-transparent bg-rose-100 text-rose-900'>
        Cancelling
      </Badge>
    )
  }
  if (ent.status === 'none') return null
  return (
    <Badge className='border-transparent bg-emerald-100 text-emerald-900'>
      Active
    </Badge>
  )
}

/* the facts read as one line either way: stacked under a small caps label on a
 * phone, inline beside it and rule-separated from here up. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex min-w-0 flex-col gap-0.5 border-border sm:flex-row sm:items-baseline sm:gap-1.5 sm:border-l sm:pl-4 sm:first:border-l-0 sm:first:pl-0'>
      <dt className='text-[10px] font-semibold uppercase tracking-wide text-ink-muted sm:text-[13px] sm:font-normal sm:normal-case sm:tracking-normal sm:text-ink-soft'>
        {label}
      </dt>
      <dd className='text-[13px] font-semibold tabular-nums text-ink sm:truncate'>
        {value}
      </dd>
    </div>
  )
}

export function PlanCard({
  overview,
  onKeepCurrentPlan,
  keepingCurrentPlan = false
}: {
  overview: BillingOverview
  onKeepCurrentPlan?: () => void
  keepingCurrentPlan?: boolean
}) {
  const { entitlements: ent, seatsUsed } = overview
  const invoiceUrl = overview.invoiceUrl
  /* while a plan is bought and unpaid, entitlements resolve to free. naming the
   * free plan here is what had someone reading "Supplier Free" off the page
   * their own unpaid Pro invoice was sitting on: the badge says it is not live
   * yet, so the card names the plan they are actually waiting on. */
  const pending = overview.pendingPlanId
  const plan = PLAN_CATALOG[pending ?? ent.planId]
  const isPaid = plan.monthlyPriceCents > 0
  const interval = ent.billingInterval ?? overview.pendingBillingInterval

  const seatLimit = pending ? plan.seatLimit : ent.seatLimit
  const seats =
    seatLimit === null ? 'Unlimited' : `${seatsUsed} of ${seatLimit}`

  const price = planPriceParts({
    plan,
    interval,
    comped: overview.isComped,
    coveredByRep: ent.billedThrough?.covered ?? false
  })

  /* the amount is already the headline, so a fact repeating it earns its place
   * only by saying when. a comped grant has no interval and no charge to name,
   * and a pending one is described by its invoice rather than a renewal that
   * has not started. */
  /* a scheduled switch takes effect on the period end, so that date is not a
   * renewal and the money charged on it is the *new* plan's, not this one's. */
  const scheduled = ent.scheduledPlanId
    ? {
        plan: PLAN_CATALOG[ent.scheduledPlanId],
        interval: ent.scheduledBillingInterval ?? ent.billingInterval
      }
    : null

  const facts: { label: string; value: string }[] = [
    { label: 'Seats', value: seats }
  ]
  if (ent.currentPeriodEnd) {
    facts.push({
      label: ent.cancelAtPeriodEnd
        ? 'Access until'
        : scheduled
          ? 'Switches'
          : 'Renews',
      value: formatBillingDate(ent.currentPeriodEnd)
    })
  }
  const charged = scheduled ?? { plan, interval: ent.billingInterval }
  if (
    isPaid &&
    !pending &&
    charged.interval &&
    charged.plan.monthlyPriceCents > 0 &&
    !ent.cancelAtPeriodEnd
  ) {
    facts.push({
      label: 'Next charge',
      value: formatMoney(
        charged.interval === 'year'
          ? charged.plan.yearlyPriceCents
          : charged.plan.monthlyPriceCents,
        'usd'
      )
    })
  }
  if (overview.invoiceDueAt) {
    facts.push({
      label: 'Invoice due',
      value: formatBillingDate(overview.invoiceDueAt)
    })
  }

  /* the scheduled phase can change cadence as well as tier, so say which. */
  const switchesTo =
    ent.scheduledPlanId && ent.currentPeriodEnd
      ? `Switches to ${PLAN_CATALOG[ent.scheduledPlanId].label} (${intervalLabel(
          ent.scheduledBillingInterval ?? ent.billingInterval
        ).toLowerCase()}) on ${formatBillingDate(ent.currentPeriodEnd)}.`
      : null

  return (
    <div className='flex flex-col gap-4'>
      {/* the amount leads, with the plan name as its eyebrow: on a free plan
          "$0 forever" is the actual answer to what you are on, where the label
          alone left the block with nothing to say. */}
      <div className='flex flex-col gap-1.5'>
        <div className='flex flex-wrap items-center gap-2'>
          <span className='font-display text-[19px] leading-none text-ink'>
            {plan.label}
          </span>
          <StatusBadge overview={overview} />
          {interval && (
            <Badge variant='outline'>{intervalLabel(interval)}</Badge>
          )}
          {overview.collectionMethod === 'send_invoice' && (
            <Badge variant='outline'>Invoiced</Badge>
          )}
        </div>

        {/* one baseline row, so the facts sit on the same line as "forever"
            rather than floating against the middle of the amount. */}
        <div className='flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-8'>
          <div className='flex items-baseline gap-2'>
            <span className='font-display text-[52px] leading-[0.95] tracking-tight text-ink'>
              {price.amount}
            </span>
            <span className='text-[14px] font-medium text-ink-muted'>
              {price.per}
            </span>
          </div>

          {/* two columns rather than a wrapping row: on a phone the facts stay
              on one line whatever they say. */}
          <dl className='grid grid-cols-2 gap-x-3 gap-y-3 sm:flex sm:flex-wrap sm:items-baseline sm:gap-x-4'>
            {facts.map(fact => (
              <Fact key={fact.label} label={fact.label} value={fact.value} />
            ))}
          </dl>
        </div>
      </div>

      {/* they are not being charged and there is no subscription to manage, so
          saying so beats a page of buttons that all refuse. */}
      {overview.isComped && (
        <p className='rounded-lg bg-sky-50 px-3 py-2 text-[13px] text-sky-900'>
          TandemOne has given you {plan.label} at no charge
          {ent.currentPeriodEnd
            ? ` until ${formatBillingDate(ent.currentPeriodEnd)}`
            : ''}
          . You will not be billed. Contact your representative to change it.
        </p>
      )}

      {/* the bill and the sentence explaining it are one thing to act on, so
          they share a strip rather than leaving a button floating under the
          card the way every other state ends with prose. every invoice needs
          this, not just the first: an invoiced customer pays a fresh one each
          period, and that one has no pending plan behind it. */}
      {invoiceUrl && (
        <div className='flex flex-col gap-2 rounded-lg border border-border bg-cream/70 px-3 py-2.5 text-[13px] text-ink-soft sm:flex-row sm:items-center sm:justify-between'>
          <span>
            {pending
              ? `You chose ${PLAN_CATALOG[pending].label}. It starts once your invoice is paid.`
              : 'Your invoice for this period is ready to pay.'}
          </span>
          <a
            className='inline-flex h-11 shrink-0 items-center justify-center rounded-lg bg-accent px-3 font-medium text-white hover:bg-accent-mid sm:h-8'
            href={invoiceUrl}
            rel='noopener noreferrer'
            target='_blank'
            /* an installed app has no tab to come back from, so stripe's return
               lands back inside it rather than in a browser. */
            onClick={event => {
              if (!isStandaloneDisplay()) return
              event.preventDefault()
              window.location.assign(invoiceUrl)
            }}
          >
            Pay invoice
          </a>
        </div>
      )}

      {pending && !invoiceUrl && (
        <p className='text-[13px] text-ink-soft'>
          You chose {PLAN_CATALOG[pending].label}. It starts once you’re
          approved.
        </p>
      )}

      {/* amber, not cream: a scheduled downgrade is a pending loss of features
          and read as background noise against the page. */}
      {switchesTo && (
        <div className='flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900 sm:flex-row sm:items-center sm:justify-between dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100'>
          <span>{switchesTo}</span>
          {onKeepCurrentPlan && (
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='shrink-0'
              disabled={keepingCurrentPlan}
              onClick={onKeepCurrentPlan}
            >
              Keep {plan.label}
            </Button>
          )}
        </div>
      )}

      {ent.cancelAtPeriodEnd && ent.currentPeriodEnd && (
        <p className='rounded-lg bg-rose-50 px-3 py-2 text-[13px] text-rose-900'>
          Your subscription ends on {formatBillingDate(ent.currentPeriodEnd)}.
          You keep full access until then, and move to Supplier Free after.
        </p>
      )}

      {ent.seatLimit !== null && seatsUsed > ent.seatLimit && (
        <p className='rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-900'>
          You have {seatsUsed} members on a {ent.seatLimit}-seat plan. Remove{' '}
          {seatsUsed - ent.seatLimit} to stay within your plan.
        </p>
      )}
    </div>
  )
}
